import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

export type ChangeHandler<T> = (value: T) => void;
export type DebouncedChangeHandler<T> = (value: T) => void;
export type ImmediateChangeHandler<T> = (value: T) => void;

export const useDebounced = <T>(
  value: T,
  onChange: ChangeHandler<T>,
  delay: number = 1000,
) => {
  // keep a "live" display value to show
  const [displayValue, setDisplayValue] = useState<T>(value);
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  const valueRef = useRef(value);
  const displayValueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  const enqueueDebouncedValue = useMemo(
    () => debounce(setDebouncedValue, delay),
    [delay],
  );

  // keep latest onChange ref so we can decouple any onChange call from debounced value changes
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // sync display value from props, but only when user hasn't made local edits
  useEffect(() => {
    const previousValue = valueRef.current;
    valueRef.current = value;

    if (displayValueRef.current === previousValue) {
      // display still matches the last synced prop — no local edits, safe to update
      displayValueRef.current = value;
      setDisplayValue(value);
      enqueueDebouncedValue.cancel();
    }
    // otherwise: user has local edits in progress, keep their input
  }, [enqueueDebouncedValue, value]);

  // invoke latest onChange callback if debounced value's changed (ties async callback to react render cycle)
  useEffect(() => {
    if (debouncedValue !== valueRef.current) {
      onChangeRef.current(debouncedValue);
    }
  }, [debouncedValue]);

  // cleanup queue on unmount
  useEffect(() => enqueueDebouncedValue.cancel, [enqueueDebouncedValue]);

  // callback for queueing new debounced onChange call
  const handleChangeDebounced: DebouncedChangeHandler<T> = useCallback(
    nextValue => {
      displayValueRef.current = nextValue;
      setDisplayValue(nextValue);
      enqueueDebouncedValue(nextValue);
    },
    [enqueueDebouncedValue],
  );

  // callback for clearing queue and immediately executing new onChange call
  const handleChangeImmediately: ImmediateChangeHandler<T> = useCallback(
    nextValue => {
      if (nextValue !== valueRef.current) {
        displayValueRef.current = nextValue;
        setDisplayValue(nextValue);
        onChangeRef.current(nextValue); // not async, no need to use debouncedValue
      }
      enqueueDebouncedValue.cancel();
    },
    [enqueueDebouncedValue],
  );

  return useMemo(
    () =>
      [displayValue, handleChangeDebounced, handleChangeImmediately] as const,
    [displayValue, handleChangeDebounced, handleChangeImmediately],
  );
};
